import axios from "axios";
import moment from "moment";
import { AUTH_PROVIDER } from "constants/constants";
import { UserType } from "config/constants/common";
// Import separated payment services
import {
  createPaymentRecordService,
  initiatePayhereCheckout,
} from "./services/paymentService";
import { signIn, getToken, getUser } from "auth/authenticate"; // Assuming getToken is available
import {
  validateRegisteredInternationalUser,
  validateRegisteredSLUser,
} from "./services/validationService";
import { showGlobalAlert } from "./bookingUtils/globalAlert";
import BookingBridge from "./services/BookingBridge";

// =======================================================
// --- CRITICAL FIX: DATA PERSISTENCE UTILITIES ---
// (These functions are necessary to bridge the payment redirect gap)
// =======================================================

const APPOINTMENT_STORAGE_KEY = "bookingData";

/**
 * Saves essential appointment details to localStorage before payment redirect.
 */
const saveAppointmentData = (data) => {
  try {
    localStorage.setItem(APPOINTMENT_STORAGE_KEY, JSON.stringify(data));
    // console.log(
    //   "Appointment data successfully persisted for post-payment retrieval."
    // );
  } catch (e) {
    console.error("Error saving appointment data to local storage:", e);
    showGlobalAlert(
      "error",
      "alert",
      e.message || "Error saving appointment data."
    );
  }
};
// ------------------------------------------------------------------------

// =======================================================
// --- UTILITIES (Kept from previous file) ---
// =======================================================

/**
 * Utility function to generate a password based on DOB or a random number.
 * @param {string} dob - Date of birth (YYYY-MM-DD format).
 * @returns {string} Generated password.
 */
const generatePassword = (dob) => {
  if (!dob) {
    const length = 6;
    const charset = "0123456789";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  }
  const date = moment(dob);
  const year = date.format("YYYY").slice(-2);
  const month = date.format("MM");
  const day = date.format("DD");
  return `${year}${month}${day}`;
};

// =======================================================
// --- API SERVICE CALLS (Abstracted) ---
// =======================================================

/**
 * Creates a new User Account and their primary Patient Profile (Guest flow).
 */
const createGuestUserAndPatientService = async (userData) => {
  const {
    firstName,
    lastName,
    email,
    phoneNumber,
    dob,
    gender,
    address,
    timezone,
  } = userData;

  const safeDob = dob ? moment(dob).format("YYYY-MM-DD") : moment().subtract(25, "years").format("YYYY-MM-DD"); // default DOB
  // ✅ Ensure valid gender fallback to prevent Joi validation errors
  const safeGender = gender === "MALE" || gender === "FEMALE" ? gender : "MALE"; // default fallback
  const safeAddress =
    address && address.trim().length > 0 ? address : "Not Provided";

  // console.log("createGuestUserAndPatientService -> gender used:", safeGender);

  const sriLankanPhoneRegex = /^(?:\+94|0)?7\d{8}$/;
  const isSriLankan = sriLankanPhoneRegex.test(phoneNumber);

  const password = generatePassword(dob);
  // const formattedDob =  : null;

  const requestData = {
    name: `${firstName} ${lastName}`,
    email: email,
    password: password,
    phoneNumber: phoneNumber,
    dob: safeDob,
    gender: safeGender,
    address: safeAddress,
    timezone: timezone,
    authProvider: AUTH_PROVIDER.EMAIL_PASSWORD,
    userType: UserType.PATIENT,
  };

  const endpoint = isSriLankan
    ? `${process.env.REACT_APP_BACKEND_URL}guardian-user/grself/sign-up/srilanka`
    : `${process.env.REACT_APP_BACKEND_URL}guardian-user/grself/sign-up/international`;

  try {
    const response = await axios.post(endpoint, requestData);

    if (response.data?.data?.user) {
      await signIn(email, password); // Log in (sets global state/token)

      // 🌟 ALERT 1: Guest User Account Creation & Password 🌟

      showGlobalAlert(
        "success",
        "alert",
        `**Account Created** (Guest Flow): Email **${email}** registered. Temporary password: **${password}** (Please note down to login later).`
      );

      // 🌟 ALERT 2: Primary Patient Profile Creation 🌟
      showGlobalAlert(
        "success",
        "alert",
        `**Patient Profile Created**: Guardian/Self-Patient profile for **${firstName} ${lastName}** created successfully under your new account.`
      );

      return {
        user: response.data.data.user,
        token: response.data.data.token,
        password: password,
        // patientId: response.data.data.user.userId, // Assuming guest's initial patientId is their userId
        patientId:
          response.data?.data?.patient?.patientId ||
          response.data?.data?.user?.userId, // Assuming guest's initial patientId is their userId
      };
    } else {
      throw new Error("Failed to create user and patient profile.");
    }

    // catch (error) {
    //   console.error("Error creating user and patient:", error);
    //   showGlobalAlert(
    //     "error",
    //     "alert",
    //     error.response?.data?.message || "Failed to create user account."
    //   );
    //   throw new Error(
    //     error.response?.data?.message || "Failed to create user account."
    //   );
    // }
  } catch (error) {
    const backendMsg = error.response?.data?.message || error.message || "";
    console.warn("Guest creation error:", backendMsg);

    if (
      backendMsg.toLowerCase().includes("already") ||
      backendMsg.toLowerCase().includes("exists")
    ) {
      showGlobalAlert(
        "warning",
        "alert",
        "This email or phone already has an account. Returning to Step 2 so you can continue as a logged-in user."
      );

      try {
        await signIn(email, password);
        await new Promise((resolve) => setTimeout(resolve, 300)); // ⏳ ensure storage updated

        const loggedUser = getUser();
        const loggedToken = getToken();

        if (!loggedUser || !loggedUser.userId) {
          throw new Error("Sign-in completed but user not found in storage.");
        }

        return {
          user: loggedUser,
          token: loggedToken,
          password: null,
          patientId: loggedUser.userId,
          recovered: true,
        };
      } catch (signErr) {
        throw new Error(
          "Existing account detected, but auto-login failed. Please log in manually."
        );
      }
    }

    showGlobalAlert(
      "error",
      "alert",
      backendMsg || "Failed to create user account."
    );
    throw new Error(backendMsg || "Failed to create user account.");
  }
};

/**
 * Creates a new Patient record under an existing User Account (e.g., Dependent).
 */

const createPatientService = async (patientData, token) => {
  const endpoint = `${process.env.REACT_APP_BACKEND_URL}guardian-user/dependent/sign-up/patient`;

  const requestData = {
    name: `${patientData.firstName} ${patientData.lastName}`,
    dob: moment(patientData.dob).format("YYYY-MM-DD"),
    gender: patientData.gender,
    address: patientData.address,
    // parentId: patientData.userId, // Link patient to the guardian/user
    guardianId: patientData.userId, // <--- legacy field name restored for safety
  };

  try {
    const response = await axios.post(endpoint, requestData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const dependent =
      response.data?.data?.dependent || response.data?.data || null;

    if (dependent?.patientId) {
      showGlobalAlert(
        "success",
        "alert",
        `**Dependent Profile Created**: Patient profile for **${patientData.firstName} ${patientData.lastName}** created successfully under your account.`
      );

      return { patientId: dependent.patientId };
    } else {
      console.warn("Unexpected API response format:", response.data);
      throw new Error("Failed to create patient record.");
    }
  } catch (error) {
    console.error("Error creating patient:", error);
    showGlobalAlert(
      "error",
      "alert",
      error.response?.data?.message || "Failed to create patient profile."
    );
    throw new Error(
      error.response?.data?.message || "Failed to create patient record."
    );
  }
};



// const createGuestDependentService = async (userData, isSriLankan) => {
//   const endpoint = isSriLankan
//     ? `${process.env.REACT_APP_BACKEND_URL}guardian-user/grdepend/sign-up/srilanka`
//     : `${process.env.REACT_APP_BACKEND_URL}guardian-user/grdepend/sign-up/international`;

//   const {
//     firstName,
//     lastName,
//     email,
//     phoneNumber,
//     dob,
//     gender,
//     address,
//     timezone,
//   } = userData;

//   const password = generatePassword(dob);
//   const formattedDob = dob ? moment(dob).format("YYYY-MM-DD") : null;

//   // ✅ FIXED: payload matches backend expectation
//   const requestData = {
//     name: `${firstName} ${lastName}`, // guardian full name
//     email,
//     password,
//     phoneNumber,
//     timezone,
//     authProvider: AUTH_PROVIDER.EMAIL_PASSWORD,
//     userType: "GUARDIAN", // ✅ required by backend
//     dependPatientDetails: {
//       // ✅ nested dependent details
//       name: `${firstName} ${lastName}`,
//       dob: formattedDob,
//       gender,
//       address,
//     },
//   };

//   try {
//     const response = await axios.post(endpoint, requestData);
//     if (response.data?.data?.user && response.data?.data?.dependent) {
//       const { user, dependent, token } = response.data.data;

//       showGlobalAlert(
//         "success",
//         "alert",
//         `**Account Created** for ${firstName} ${lastName}. Temporary password: **${password}**`
//       );

//       return {
//         user,
//         dependent,
//         token,
//         password,
//       };
//     } else {
//       throw new Error("Failed to create dependent patient (guest flow).");
//     }
//   } catch (error) {
//     console.error("Error in createGuestDependentService:", error);
//     showGlobalAlert(
//       "error",
//       "alert",
//       error.response?.data?.message || "Failed to create guest dependent."
//     );
//     throw error;
//   }
// };


/**
 * Creates the Appointment record.
 */
const createAppointmentService = async (authStatus, appointmentData, token) => {
  const { doctorId, patientId, date, slotTime, amount, parentId } =
    appointmentData;

  // // Ensure full ISO 8601 UTC timestamp (SIMPLIFIED) ---
  // // slotTime is expected to be 'HH:mm:ss.sssZ' or similar, date is 'YYYY-MM-DD'.
  // // Combine them and ensure the Z suffix.
  // let fullUtcTimestamp = `${date}T${slotTime}`;

  // // Ensure the final string ends with 'Z' for UTC compliance
  // if (!fullUtcTimestamp.endsWith('Z')) {
  //     // This is defensive, in case the time part was '09:00:00.000'
  //     fullUtcTimestamp += 'Z';
  // }


// const isSandbox = process.env.REACT_APP_PAYHERE_ENV === "SANDBOX";

  const requestBody = {
    doctorId: doctorId,
    patientId: patientId,
    date: date,
    slotTime: slotTime, // Assumed to be UTC time based on legacy code
    appointmentType: "ONLINE",
    purpose: "CONSULTING",
    medicalDetails: "",
    message: "",
    amount: amount,
    // paymentId: "test_id", // Placeholder
    // paymentId: isSandbox ? "test_id" : undefined,
    paymentId: "test_id",
    parentId: parentId,
  };

  try {
    // const endpoint =
    //   authStatus === "logged_in"
    //     ? `${process.env.REACT_APP_BACKEND_URL}appointment/patient`
    //     : `${process.env.REACT_APP_BACKEND_URL}guest-user/appointment/patient`;

    const useGuestEndpoint = !token && authStatus === "guest";
    const endpoint = useGuestEndpoint
      ? `${process.env.REACT_APP_BACKEND_URL}guest-user/appointment/patient`
      : `${process.env.REACT_APP_BACKEND_URL}appointment/patient`;

    const USE_FORMDATA = true;

    let payload = requestBody;
    let headers = { Authorization: `Bearer ${token}` };

    if (USE_FORMDATA) {
      const body = new FormData();
      Object.entries(requestBody).forEach(([k, v]) => body.append(k, v));
      payload = body;
      headers["Content-Type"] = "multipart/form-data";
    }

    const response = await axios.post(endpoint, payload, { headers });

    // const response = await axios.post(endpoint, requestBody, {
    //   headers: { Authorization: `Bearer ${token}` },
    // });

    if (response.data?.data?.appointmentId) {
      return {
        appointmentId: response.data.data.appointmentId,
        data: response.data.data,
      };
    } else {
      throw new Error("Failed to create appointment.");
    }
  } catch (error) {
    console.error("Error creating appointment:", error);
    showGlobalAlert(
      "error",
      "alert",
      error.response?.data?.message || "Failed to create appointment."
    );
    throw new Error(
      error.response?.data?.message || "Failed to create appointment."
    );
  }
};

// =======================================================
// --- MAIN ORCHESTRATOR SERVICE (FIXED PERSISTENCE VARIABLES) ---
// =======================================================

/**
 * @typedef {Object} BookingResult
 * @property {boolean} [success] - Indicates booking and payment were successfully initiated.
 * @property {string} [appointmentId] - Created appointment ID.
 * @property {string} [message] - Human-readable message.
 * @property {string|null} [password] - Temporary password (if new guest account).
 * @property {boolean} [recovered] - Indicates a fallback flow where an existing account was detected and reused.
 */

/**
 * @param {object} state
 * @param {object} appointmentData
 * @returns {Promise<BookingResult>}
 */

const submitBookingAndCheckoutService = async (
  state,
  appointmentData
) => {
  // =======================================================
  // BRIDGE SNAPSHOT + DOUBLE SUBMIT GUARD
  // =======================================================
  if (!BookingBridge.beginSubmit()) {
    console.warn(
      "Booking submission already in progress — ignoring duplicate click."
    );
    return { success: false, message: "Submission already in progress." };
  }

  // Get the freshest snapshot of current booking data from provider
  const snap = BookingBridge.getSnapshot();

  // Merge the latest context state with passed-in arguments
  const stateMerged = {
    ...state,
    contactPhone:
      snap.contactDetails?.verifiedPhone ||
      snap.contactDetails?.enteredPhone ||
      state?.contactPhone,
    contactEmail:
      snap.contactDetails?.verifiedEmail ||
      snap.contactDetails?.enteredEmail ||
      state?.contactEmail,
    authStatus: snap.bookingPerson?.authStatus || state?.authStatus,
    authPatient: snap.bookingPerson || state?.authPatient,
    appointmentPatient: snap.patient || state?.appointmentPatient,
  };

  const appointmentMerged = {
    ...appointmentData,
    doctorId: snap.doctorDetails?.doctorId || appointmentData?.doctorId,
    doctorName: snap.doctorDetails?.doctorName || appointmentData?.doctorName,
    displayingFee:
      snap.doctorDetails?.displayingFee || appointmentData?.displayingFee,
    selectedTimeZone: snap.userTimeZone || appointmentData?.selectedTimeZone,
  };

  // From here on, use `stateMerged` and `appointmentMerged` instead of the originals
  const {
    contactPhone,
    contactEmail,
    authStatus,
    guestUser,
    authPatient,
    appointmentPatient,
  } = stateMerged;

  // const {
  //   contactPhone,
  //   contactEmail,
  //   authStatus,
  //   guestUser = {},
  //   authPatient = {},
  //   appointmentPatient = {},
  // } = state;

  let token = getToken();
  let userId = authPatient?.id;
  let patientIdForBooking = appointmentPatient?.patientId;

  // --- safeguard for logged-in user booking for self ---
  if (
    authStatus === "logged_in" &&
    appointmentPatient.bookingFor === "myself" &&
    !patientIdForBooking &&
    authPatient?.patientId
  ) {
    patientIdForBooking = authPatient.patientId;
  }

  // --- CRITICAL STEP: Define Payer Details (Guardian/User) for Checkout ---
  let payerDetails;
  if (authStatus === "logged_in") {
    // Payer is the logged-in user/guardian.
    payerDetails = {
      ...authPatient,
      // firstName: authPatient?.name?.firstName || authPatient?.username || "",
      // lastName: authPatient?.name?.lastName || authPatient?.username || "",
      firstName:
        authPatient?.name?.firstName ??
        authPatient?.firstName ??
        authPatient?.username ??
        "",
      lastName:
        authPatient?.name?.lastName ??
        authPatient?.lastName ??
        authPatient?.username ??
        "",

      dob: appointmentPatient.birthDate || appointmentPatient.dob || "",
      address: appointmentPatient.address || "",
    };
  } else {
    // Payer is the guest user. Their identity is split across states:
    payerDetails = {
      ...guestUser, // email, phone, firstName, lastName
      address: appointmentPatient.address || "", // Payer's Address
      dob: appointmentPatient.dob || appointmentPatient.birthDate || "", // Payer's DOB
    };
  }

  try {
    let guestResponse = null;

    if (!contactEmail || !contactPhone) {
      console.error("❌ Missing contact details for guest:", {
        contactEmail,
        contactPhone,
      });

      throw new Error(
        "Verified contact details missing. Please re-verify contact before continuing."
      );
    }

    const sriLankanPhoneRegex = /^(?:\+94|0)?7\d{8}$/;

    // --- STEP 1: HANDLE GUEST USER & PRIMARY PATIENT CREATION (If Guest) ---
    if (authStatus === "guest") {

        const safeGender =
          appointmentPatient.gender === "MALE" ||
          appointmentPatient.gender === "FEMALE"
            ? appointmentPatient.gender
            : "MALE";

        const safeAddress =
          appointmentPatient.address &&
          appointmentPatient.address.trim().length > 0
            ? appointmentPatient.address
            : "Not Provided";

        const safeDob =
          appointmentPatient.dob ||
          appointmentPatient.birthDate ||
          moment().subtract(25, "years").format("YYYY-MM-DD");

      guestResponse = await createGuestUserAndPatientService({
        // firstName: appointmentPatient.firstName,
        // lastName: appointmentPatient.lastName,
        // email: contactEmail, // ✅ ADD THIS
        // phoneNumber: contactPhone, // ✅ ADD THIS
        // dob: appointmentPatient.dob || appointmentPatient.birthDate,
        // gender: appointmentPatient.gender,
        // address: appointmentPatient.address,
        // timezone: appointmentMerged.selectedTimeZone,
        firstName: guestUser.firstName, // guardian's name from Step 1
        lastName: guestUser.lastName,
        email: contactEmail,
        phoneNumber: contactPhone,
        // Guardian (user) shouldn’t inherit dependent’s demographics
        dob: safeDob,
        gender: safeGender,
        address: safeAddress,
        timezone: appointmentMerged.selectedTimeZone,
      });

      if (guestResponse?.recovered) {
        // Early exit for already-existing user; skip appointment creation
        return { recovered: true };
      }

      token = guestResponse.token;
      userId = guestResponse.user.userId;

      // Update payerDetails with the final user object and details for the payment SDK
      payerDetails = {
        ...guestResponse.user,
        // firstName: guestUser.firstName,
        // lastName: guestUser.lastName,
        firstName: guestUser?.firstName ?? appointmentPatient.firstName,
        lastName: guestUser?.lastName ?? appointmentPatient.lastName,
        phoneNumber: contactPhone,
        email: contactEmail,
        address: appointmentPatient.address,
      };

      // If guest is booking for "myself," the patient ID is the newly created one.
      if (appointmentPatient.bookingFor === "myself") {
        patientIdForBooking = guestResponse.patientId;
      }
    }

    // --- STEP 2: HANDLE NEW PATIENT CREATION (If Dependent or Someone Else) ---
    if (appointmentPatient.bookingFor === "someone_else") {
      // ✅ Reuse the guardian/guest user created in Step 1.
      // Only create a dependent patient profile linked to that user.
      const newDependent = await createPatientService(
        {
          userId: userId, // guardian’s userId from Step 1
          firstName: appointmentPatient.firstName,
          lastName: appointmentPatient.lastName,
          dob: appointmentPatient.dob,
          gender: appointmentPatient.gender,
          address: appointmentPatient.address,
        },
        token
      );
      patientIdForBooking = newDependent.patientId;
    }

    // 1: CREATE APPOINTMENT
    if (!patientIdForBooking) {
      // console.log("DEBUG bookingService ID", {
      //   authStatus,
      //   userId,
      //   patientIdForBooking,
      //   authPatientId: authPatient?.patientId,
      //   appointmentPatientId: appointmentPatient?.patientId,
      //   bookingFor: appointmentPatient?.bookingFor,
      // });

      throw new Error(
        "Could not determine a valid patient ID for the booking."
      );
    }

    const appointmentResponse = await createAppointmentService(
      authStatus,
      {
        doctorId: appointmentMerged.doctorId,
        patientId: patientIdForBooking,
        date: appointmentMerged.selectedDate,
        slotTime: appointmentMerged.selectedTimeSlot, // UTC time
        amount: appointmentMerged.displayingFee,
        parentId: userId, // The user/guardian who initiated the booking
      },
      token
    );

    const appointmentId = appointmentResponse.appointmentId;

    // 🌟 ALERT 4: Appointment Creation 🌟

    showGlobalAlert(
      "success",
      "alert",
      `**Appointment Created**: Appointment created successfully. Appointment ID: **${appointmentId}**.`
    );

    // 2: CREATE PAYMENT RECORD
    const { paymentRef, hash } = await createPaymentRecordService(
      appointmentId,
      appointmentMerged.displayingFee,
      userId, // Use the Payer's userId for the payment record
      token
    );

    // 🌟 ALERT 5: Payment Record Creation 🌟

    showGlobalAlert(
      "success",
      "alert",
      `**Payment Record Created**: Payment reference **${paymentRef}** generated. Redirecting to secure payment gateway...`
    );

    // =======================================================
    // 3. CRITICAL DATA FLOW FIX: PERSIST ESSENTIAL DATA
    //    FIXED: Using variables from 'appointmentData' argument.
    // =======================================================
    const dataToPersist = {
      appointmentId: appointmentId,
      // Using correct variables from the function argument:
      appointmentDateUTC: appointmentMerged.selectedDate,
      appointmentTimeUTC: appointmentMerged.selectedTimeSlot,
      doctorName: appointmentMerged.doctorName, // Assuming this exists on the initial appointmentMerged object
      localTimezone: appointmentMerged.selectedTimeZone, // Assuming this exists on the initial appointmentData object
      tempPassword: guestResponse?.password || null, // ✅ Add this line
    };
    saveAppointmentData(dataToPersist);
    // =======================================================

    // Small delay to give users time to copy their password before PayHere redirect
    if (guestResponse?.password) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    // 4. INITIATE PAYHERE CHECKOUT
    await initiatePayhereCheckout({
      payerDetails: payerDetails,
      fee: appointmentMerged.displayingFee,
      paymentRef: paymentRef,
      hash: hash,
      appointmentId: appointmentId,
    });

    // try {
    //   localStorage.removeItem(APPOINTMENT_STORAGE_KEY);
    // } catch (cleanupErr) {
    //   console.warn("Failed to clean bookingData:", cleanupErr);
    // }
    // The checkout resolved successfully (meaning redirect was triggered)
    const finalPassword = guestResponse?.password || null;

    return {
      success: true,
      appointmentId: appointmentId,
      message: "Payment initiated. Please complete the checkout.",
      password: finalPassword,
    };
  } catch (error) {
    console.error("Booking Submission Orchestration Error:", error);
    showGlobalAlert(
      "error",
      "alert",
      error.response?.data?.message || "Failed to finalize the booking."
    );
    throw new Error(
      error.message ||
        "Failed to finalize the booking due to an unexpected error."
    );
  } finally {
    // Always release the submission lock
    BookingBridge.endSubmit();
  }
};
// =======================================================
// --- LEGACY/STEP-SPECIFIC SERVICES (Keeping for reference/dependency) ---
// =======================================================

/**
 * Handles the contact input submission, calling the real API to check for an existing user.
 */
const handleContactSubmissionService = async (
  contactData,
  currentAuthStatus,
  setAuthStatus,
  isSLMobile,
  isSLLandline
) => {
  if (!contactData.phone || !contactData.email) {
    return {
      type: "ERROR",
      message: "Phone and Email are required to proceed.",
    };
  }

  try {
    let validationResult = { isExisting: false };
    const { phone, email } = contactData;

    const isSLPhone = isSLMobile(phone) || isSLLandline(phone);

    // API Call Logic
    if (isSLPhone) {
      let slApiPhone = phone.startsWith("+94")
        ? "0" + phone.substring(3)
        : phone;
      validationResult = await validateRegisteredSLUser(email, slApiPhone);
    } else {
      validationResult = await validateRegisteredInternationalUser(
        email,
        phone
      );
    }

    if (validationResult.isExisting === true) {
      if (validationResult.field === "EMAIL") {
        return { type: "EMAIL_WARNING" };
      }

      const status = "registered_needs_verification";
      if (currentAuthStatus !== "logged_in") {
        // console.log(
        //   "User found by phone/general check. Setting status to 'registered_needs_verification'."
        // );
        setAuthStatus(status);
      }
      return { type: "SUCCESS", newStatus: status };
    } else {
      const status = "guest";
      setAuthStatus(status);
      return { type: "SUCCESS", newStatus: status };
    }
  } catch (error) {
    console.error(
      "API Error during contact submission:",
      error.response || error.message
    );
    showGlobalAlert(
      "error",
      "alert",
      error.response?.data?.message ||
        "Failed to submit the contact due to server error."
    );

    if (error.response && error.response.status >= 500) {
      return {
        type: "ERROR",
        message: "Server error occurred. Please try again later.",
      };
    }

    setAuthStatus("guest");
    return null;
  }
};

/**
 * Handles the logic after OTP verification for a found, registered user.
 */
const handleRegisteredLoginService = async (
  email,
  setAuthStatus,
  setStepPage
) => {
  // console.log(`Simulating external login for verified user: ${email}`);

  await new Promise((resolve) => setTimeout(resolve, 500));

  setAuthStatus("logged_in");
  setStepPage(2, 0);

  return null;
};

/**
 * Handles the OTP submission, simulating verification against a backend service.
 */
const handleOtpSubmissionService = (
  otp,
  isGuest,
  isRegistered,
  setStepPage,
  setAuthStatus
) => {
  if (otp !== "1234") {
    return "Invalid OTP. Try 1234.";
  }

  if (isGuest) {
    setStepPage(1, 3);
  } else if (isRegistered) {
    setAuthStatus("logged_in");
  }

  return null;
};

/**
 * Handles the Guest Name submission.
 */
const handleNameSubmissionService = (
  fname,
  lname,
  updateGuestUserData,
  setStepPage
) => {
  if (!fname.trim()) {
    return "First Name is required.";
  }
  if (!lname.trim()) {
    return "Last Name is required.";
  }

  updateGuestUserData("firstName", fname.trim());
  updateGuestUserData("lastName", lname.trim());
  setStepPage(2, 0);

  return null;
};

/**
 * Handles the submission and validation of patient details form (Step 2).
 */
// bookingServices.js

/**
 * Final validation and data preparation before Step 3.
 * This function ensures the patient data is logically valid and consistent
 * with the current booking context.
 **/

// const handleStep2SubmissionService = (patientData) => {
//   if (!patientData) return "Invalid patient data provided.";

//   const { bookingFor, firstName, lastName, address, patientId, parentId } =
//     patientData;

//   // --- Basic logical checks ---
//   if (!bookingFor)
//     return "Please specify who this appointment is for (myself, dependent, or someone else).";

//   if (!firstName || !lastName)
//     return "Please provide the patient's full name before continuing.";

//   if (!address) return "Please enter the patient's address.";

//   // --- Specific validation by booking type ---
//   switch (bookingFor) {
//     case "myself":
//       // For authenticated users, patientId must exist
//       if (!patientId && authStatus === "logged_in")
//         return "Unable to identify your account. Please log in or verify contact details again.";
//       break;

//     case "dependent":
//       // Must have valid dependent ID and parent linkage
//       if (!patientId)
//         return "Dependent information missing. Please re-select the dependent.";
//       if (!parentId)
//         return "Parent ID missing. Please log in again before booking for a dependent.";
//       break;

//     case "someone_else":
//       // Someone else must have name/address, but no patientId yet.
//       if (patientId)
//         return "New dependent should not already have a patient ID.";
//       // If logged in user, ensure parent link is valid.
//       // If guest, parentId can remain null.
//       break;

//     default:
//       return "Invalid booking type selected. Please start over.";
//   }

//   // --- Optional: Format cleanup ---
//   patientData.firstName = patientData.firstName.trim();
//   patientData.lastName = patientData.lastName.trim();
//   patientData.address = patientData.address.trim();

//   // If you need to generate a temporary ID for new dependents (guest or logged-in)
//   if (!patientData.patientId && bookingFor !== "dependent") {
//     patientData.tempId = `TMP-${Date.now()}`;
//   }

//   // ✅ If all checks pass
//   return null;
// };

const handleStep2SubmissionService = (patientData, authStatus) => {
  if (!patientData) return "Invalid patient data provided.";

  const { bookingFor, firstName, lastName, address, patientId, parentId } =
    patientData;

  if (!bookingFor)
    return "Please specify who this appointment is for (myself, dependent, or someone else).";
  if (!firstName || !lastName)
    return "Please provide the patient's full name before continuing.";
  if (!address) return "Please enter the patient's address.";

  switch (bookingFor) {
    case "myself":
      // Only enforce patientId if logged in user
      if (authStatus === "logged_in" && !patientId)
        return "Unable to identify your account. Please log in or verify contact details again.";
      break;

    case "dependent":
      if (!patientId)
        return "Dependent information missing. Please re-select the dependent.";
      if (!parentId)
        return "Parent ID missing. Please log in again before booking for a dependent.";
      break;

    case "someone_else":
      if (patientId)
        return "New dependent should not already have a patient ID.";
      break;

    default:
      return "Invalid booking type selected. Please start over.";
  }

  return null;
};


export {
  submitBookingAndCheckoutService,
  createGuestUserAndPatientService,
  createPatientService,
  createAppointmentService,
  handleContactSubmissionService,
  handleRegisteredLoginService,
  handleOtpSubmissionService,
  handleNameSubmissionService,
  handleStep2SubmissionService,
};
